import {CreateIssueButton} from '@github-ui/issue-create/CreateIssueButton'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {useRouteQuery, useSuspenseRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {Link as RouterLink} from '@github-ui/react-router'
import {RepoIssuesPageLayout} from '@github-ui/repo-issues-page-layout/RepoIssuesPageLayout'
import {RepoIssuesSidebarLayoutEnabledContext} from '@github-ui/repo-issues-page-layout/useSidebarLayoutEnabled'
import type {SavedViewsResponse, UserShortcut} from '@github-ui/saved-views'
import {ViewsBackLink} from '@github-ui/saved-views/components/ViewsBackLink'
import {BUTTON_LABELS} from '@github-ui/saved-views/constants'
import {RepositorySavedViewContent} from '@github-ui/saved-views/components/detail/RepositorySavedViewContent'
import {ViewsIndexContent} from '@github-ui/saved-views/components/index/ViewsIndexContent'
import {RepositoryViewNewContent} from '@github-ui/saved-views/components/new/RepositoryViewNewContent'
import {SavedViewIssueStatusProvider} from '@github-ui/saved-views/contexts/SavedViewIssueStatusContext'
import {useConfirmDiscardChanges} from '@github-ui/saved-views/hooks/use-confirm-discard-changes'
import {useSavedViewEditor} from '@github-ui/saved-views/hooks/use-saved-view-editor'
import {useNavigate} from '@github-ui/use-navigate'
import {Button} from '@primer/react'
import {createContext, use, useCallback, useMemo} from 'react'

import {ConnectedSavedViewOptionsMenu} from '../components/saved-view-options/ConnectedSavedViewOptionsMenu'
import {ConnectedSavedViewUrlConfiguration} from '../components/saved-view-options/ConnectedSavedViewUrlConfiguration'
import {viewConfigurationFromPayload} from '../components/saved-view-options/SavedViewUrlConfiguration'
import {useNewViewConfiguration} from '../components/saved-view-options/use-new-view-configuration'
import {SavedViewLayoutSwitch} from '../components/SavedViewLayoutSwitch'
import {
  useSavedViewConfiguration,
  useSavedViewConfigurationDirtyState,
} from '../contexts/views/SavedViewConfigurationProvider'
import type {ViewConfiguration} from '../contexts/views/view-configuration'
import {useClearSavedViewOverrides} from '../hooks/use-clear-saved-view-overrides'
import {type SavedViewNavigation, useSavedViewNavigation} from '../hooks/use-saved-view-navigation'
import {useEvictSavedViewPrefetches} from '../hooks/use-saved-view-prefetch'
import {issuesLayoutRoute, layoutIssueStatusSearchOptions} from './issues-layout-route'
import {useRenderSidebar} from './render-issues-sidebar'
import {savedViewNewRoute, savedViewRoute, savedViewsRoute} from './saved-views-routes'
import {REPOSITORY_SAVED_VIEW_BOARD_LAYOUT, RepositorySavedViewBoard} from './RepositorySavedViewBoard'
import {type SavedViewRoutePayload, savedViewRouteSearchParams} from './saved-view-route-payload'
import {TanStackSavedViewIssues} from './TanStackSavedViewIssues'
import {useSavedViewContentForQuery} from './use-saved-view-content-for-query'

const SAVED_VIEW_ROUTE_READERS = {mainQuery: savedViewRouteSearchParams}

const INITIAL_SHORTCUT: UserShortcut = {
  id: '',
  name: '',
  searchType: 'issues',
  query: 'is:issue state:open',
  description: '',
  icon: 'bookmark',
  color: 'gray',
}

function useSavedViewsLayout(initialSavedViews?: SavedViewsResponse) {
  const {owner, repo} = useRouteParams(issuesLayoutRoute)
  const {data: layoutPayload} = useRouteQuery(issuesLayoutRoute, 'mainQuery')
  return {
    owner,
    repo,
    layoutPayload,
    issueStatusSearchOptions: useMemo(() => layoutIssueStatusSearchOptions(layoutPayload), [layoutPayload]),
    renderSidebar: useRenderSidebar(owner, repo, layoutPayload, initialSavedViews),
  }
}

export function SavedViews() {
  const {data: savedViews} = useRouteQuery(savedViewsRoute, 'mainQuery')
  const {owner, repo, layoutPayload, renderSidebar} = useSavedViewsLayout(savedViews)
  const newViewUrl = `/${owner}/${repo}/issues/views/new`
  const newViewAction =
    (savedViews?.viewerCanManageViews ?? false) ? (
      <Button as={RouterLink} to={newViewUrl} variant="primary">
        {BUTTON_LABELS.newView}
      </Button>
    ) : undefined

  return (
    <RepoIssuesSidebarLayoutEnabledContext value>
      <RepoIssuesPageLayout
        title="Views"
        dialogTitle="Views"
        actions={newViewAction}
        renderSidebar={renderSidebar}
        initialExpanded={layoutPayload?.sidebarExpanded}
      >
        <ViewsIndexContent owner={owner} repo={repo} newViewUrl={newViewUrl} pinEnabled initialData={savedViews} />
      </RepoIssuesPageLayout>
    </RepoIssuesSidebarLayoutEnabledContext>
  )
}

export function SavedViewNew() {
  const {owner, repo, layoutPayload, issueStatusSearchOptions, renderSidebar} = useSavedViewsLayout()
  const navigate = useNavigate()
  const viewsPath = `/${owner}/${repo}/issues/views`
  const {data: board} = useRouteQuery(savedViewNewRoute, 'mainQuery')
  const newViewConfiguration = useNewViewConfiguration({
    defaultConfiguration: board?.boardEnabled ? board.defaultConfiguration : undefined,
    basePath: viewsPath,
    routeId: savedViewNewRoute.id,
    boardLayout: REPOSITORY_SAVED_VIEW_BOARD_LAYOUT,
  })
  const editor = useSavedViewEditor({initialShortcut: INITIAL_SHORTCUT})
  const isDirty = editor.isDirty || newViewConfiguration.isDirty
  const {confirmDiscardIfDirty} = useConfirmDiscardChanges({isDirty})

  const handleBackClick = useCallback<React.MouseEventHandler<HTMLAnchorElement>>(
    async event => {
      if (event.defaultPrevented || event.button !== 0) return
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || !isDirty) return

      event.preventDefault()
      if (await confirmDiscardIfDirty()) navigate(viewsPath)
    },
    [confirmDiscardIfDirty, isDirty, navigate, viewsPath],
  )

  return (
    <RepoIssuesSidebarLayoutEnabledContext value>
      <RepoIssuesPageLayout
        dialogTitle="Views"
        title="Create view"
        contextArea={
          board?.boardEnabled ? undefined : <ViewsBackLink viewsPath={viewsPath} onClick={handleBackClick} />
        }
        renderSidebar={renderSidebar}
        initialExpanded={layoutPayload?.sidebarExpanded}
        // As wide as a saved view's page, so the preview is drawn the same size.
        width={board?.boardEnabled ? 'full' : undefined}
      >
        <SavedViewIssueStatusProvider issueStatusSearchOptions={issueStatusSearchOptions}>
          <RepositoryViewNewContent<ViewConfiguration>
            editor={editor}
            confirmDiscardIfDirty={confirmDiscardIfDirty}
            IssuesRenderer={TanStackSavedViewIssues}
            configurationFields={newViewConfiguration.configurationFields}
            configuration={newViewConfiguration.configuration}
            preview={newViewConfiguration.renderBoardPreview}
          />
        </SavedViewIssueStatusProvider>
      </RepoIssuesPageLayout>
    </RepoIssuesSidebarLayoutEnabledContext>
  )
}

export function SavedView() {
  const {owner, repo, layoutPayload, issueStatusSearchOptions, renderSidebar} = useSavedViewsLayout()
  const params = useRouteParams(savedViewRoute)
  const {data: route, dataUpdatedAt} = useSuspenseRouteQuery(savedViewRoute, 'mainQuery')
  const basePath = `/${owner}/${repo}/issues/views`
  const applied = useMemo(() => appliedConfiguration(route), [route])

  const contentProps = {
    pinEnabled: true,
    renderSidebar,
    renderCreateIssueButton: () => <RepositoryCreateIssueButton owner={owner} repo={repo} />,
    initialData: route.view,
    initialDataUpdatedAt: route.view ? dataUpdatedAt : undefined,
    width: route.boardEnabled ? ('full' as const) : undefined,
    initialExpanded: layoutPayload?.sidebarExpanded,
  }

  // The server says whether this repository offers the board, and that gates the whole
  // view-options experience. Off, none of it mounts.
  return (
    <RepoIssuesSidebarLayoutEnabledContext value>
      <SavedViewIssueStatusProvider issueStatusSearchOptions={issueStatusSearchOptions}>
        {route.boardEnabled ? (
          <ConnectedSavedViewUrlConfiguration
            route={savedViewRoute}
            params={params}
            applied={applied}
            baseline={route.configuration}
            basePath={basePath}
          >
            <ConfiguredSavedViewContent
              {...contentProps}
              basePath={basePath}
              saveConfiguration={route.configuration !== undefined}
            />
          </ConnectedSavedViewUrlConfiguration>
        ) : (
          <RepositorySavedViewContent IssuesRenderer={TanStackSavedViewIssues} {...contentProps} />
        )}
      </SavedViewIssueStatusProvider>
    </RepoIssuesSidebarLayoutEnabledContext>
  )
}

/**
 * The configuration the page is drawn with: the served layout and board axes over the stored
 * settings. Read from the response rather than the URL, because the server can ignore or fall back
 * on what the URL asks for.
 */
function appliedConfiguration(route: SavedViewRoutePayload): ViewConfiguration {
  return viewConfigurationFromPayload(
    {
      layout: route.layout,
      groupBy: route.layout === 'board' ? route.content.columnField : (route.configuration?.groupBy ?? null),
      swimlaneBy: route.layout === 'board' ? route.content.rowField : (route.configuration?.swimlaneBy ?? null),
      sorts: route.layout === 'board' ? route.content.sorts : undefined,
    },
    route.configuration,
  )
}

/**
 * The loading navigation for the view on screen. `RepositorySavedViewContent` mounts the issues
 * renderer separately in the read view and the edit form, so the placeholder delay is read above both.
 */
const SavedViewNavigationContext = createContext<SavedViewNavigation | null>(null)

function ConfiguredSavedViewContent({
  basePath,
  saveConfiguration,
  ...props
}: Omit<
  React.ComponentProps<typeof RepositorySavedViewContent<ViewConfiguration>>,
  | 'IssuesRenderer'
  | 'filterBarActions'
  | 'configuration'
  | 'isConfigurationDirty'
  | 'onSaveSuccess'
  | 'onEditSuccess'
  | 'onEditStart'
  | 'onEditCancel'
  | 'editConfigurationFields'
  | 'editConfigurationChanged'
  | 'isNavigating'
> & {
  basePath: string
  /** Whether Save sends the configuration. Off when the server sent no stored one to compare against. */
  saveConfiguration: boolean
}) {
  const configuration = useSavedViewConfiguration()
  const {isConfigurationDirty} = useSavedViewConfigurationDirtyState()
  const {data: route} = useSuspenseRouteQuery(savedViewRoute, 'mainQuery')
  const navigation = useSavedViewNavigation(route)
  const evictPrefetches = useEvictSavedViewPrefetches(savedViewRoute, savedViewRouteSearchParams)
  const {onSaveSuccess, onEditSuccess, onEditStart, onEditCancel, editConfigurationChanged} =
    useClearSavedViewOverrides({
      route: savedViewRoute,
      requestSearchParams: SAVED_VIEW_ROUTE_READERS,
      evictPrefetches,
      configuration,
    })

  return (
    <SavedViewNavigationContext value={navigation}>
      <RepositorySavedViewContent<ViewConfiguration>
        {...props}
        IssuesRenderer={SavedViewIssuesForLayout}
        filterBarActions={({isSaving}) => <ConnectedSavedViewOptionsMenu basePath={basePath} disabled={isSaving} />}
        editConfigurationFields={
          saveConfiguration
            ? ({isSaving}) => (
                <ConnectedSavedViewOptionsMenu basePath={basePath} disabled={isSaving} restoreBoardPicks />
              )
            : undefined
        }
        configuration={saveConfiguration ? configuration : undefined}
        isConfigurationDirty={isConfigurationDirty}
        onSaveSuccess={onSaveSuccess}
        onEditSuccess={onEditSuccess}
        onEditStart={onEditStart}
        onEditCancel={onEditCancel}
        editConfigurationChanged={editConfigurationChanged}
        isNavigating={navigation.isNavigating}
      />
    </SavedViewNavigationContext>
  )
}

function RepositoryCreateIssueButton({owner, repo}: {owner: string; repo: string}) {
  const navigate = useNavigate()

  return (
    <CreateIssueButton
      label="New issue"
      navigate={navigate}
      optionConfig={{
        issueCreateArguments: {repository: {owner, name: repo}},
        showRepositoryPicker: false,
        showFullScreenButton: true,
        canBypassTemplateSelection: true,
        navigate,
      }}
    />
  )
}

/** Renders the layout the server served, or a placeholder for a layout that is loading. */
function SavedViewIssuesForLayout({
  owner,
  repo,
  query,
  preheatEnabled,
}: {
  owner: string
  repo: string
  query: string
  preheatEnabled?: boolean
}) {
  const {id} = useRouteParams(savedViewRoute)
  const {route, dataUpdatedAt, isFetching, isError} = useSavedViewContentForQuery(query)
  const navigation = use(SavedViewNavigationContext)
  if (navigation === null) throw new Error('SavedViewIssuesForLayout requires ConfiguredSavedViewContent')

  return (
    <SavedViewLayoutSwitch
      boardContainerStyle={REPOSITORY_SAVED_VIEW_BOARD_LAYOUT.containerStyle}
      layout={route.layout === 'board' && !isError ? 'board' : 'list'}
      navigation={navigation}
      renderBoard={board =>
        route.layout === 'board' && (
          <RepositorySavedViewBoard
            board={route.content}
            owner={owner}
            repo={repo}
            id={id}
            query={query}
            savedQuery={route.view?.savedView.query}
            dataUpdatedAt={route.boardDataUpdatedAt ?? dataUpdatedAt}
            isNavigating={board.isNavigating || isFetching}
            basePath={`/${owner}/${repo}/issues/views`}
          />
        )
      }
      renderList={() => (
        <TanStackSavedViewIssues owner={owner} repo={repo} query={query} preheatEnabled={preheatEnabled} />
      )}
    />
  )
}
